import axios from 'helpers/axios';
import { isValidEmail } from 'helpers/form';
import { useToasts } from 'hooks';
import { MemberRole, MemberRoles } from 'models';
import React from 'react';
import { IconButton } from 'ui/components/shared';
import { Button, Container, Form, Label, Select, TextInput, Title } from 'ui/components/styled';
import _ from 'lodash';

export const AddMembers = ({ company, onClose, onSuccess }) => {
  const [state, setState] = React.useState([
    { name: '', email: '', role: MemberRole.User, isNameDisabled: true },
  ]);
  const [preventSubmit, setPreventSubmit] = React.useState(false);
  const { errorToast, successToast, removeAllToasts } = useToasts();

  const addMember = () => {
    setState((state) => [
      ...state,
      { name: '', email: '', role: MemberRole.User, isNameDisabled: true },
    ]);
  };

  const removeMember = (index) => {
    setState((state) => state.filter((_, i) => i !== index));
  };

  const searchUserByEmail = async (email, index) => {
    try {
      const response = await axios.get(`/uext/users/search/${email}`);
      const user = response.data;
      if (user)
        setState((state) => {
          const newState = [...state];
          newState[index].name = `${user.name.first} ${user.name.last}`;
          newState[index].isNameDisabled = true;
          return newState;
        });
      else
        setState((state) => {
          const newState = [...state];
          newState[index].name = '';
          newState[index].isNameDisabled = false;
          return newState;
        });
    } catch (error) {
      errorToast('Error while fetching user by email');
    }
  };

  const handleChange = (e) => {
    const {
      name,
      value,
      dataset: { index },
    } = e.target;
    state[index][name] = value;
    if (name === 'email' && !isValidEmail(value)) {
      state[index].name = '';
      state[index].isNameDisabled = true;
    }
    if (isValidEmail(value) && name === 'email') {
      searchUserByEmail(value, index);
    }
    setState([...state]);
  };

  const handleEmailBlur = (e) => {
    const {
      value,
      dataset: { index },
    } = e.target;
    const isEmailValid = isValidEmail(value);
    if (isEmailValid) searchUserByEmail(value, index);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setPreventSubmit(true);
      const response = await axios.post(
        `/companies/${company?.id}/members/many`,
        state.map((item) => _.omit(item, 'isNameDisabled'))
      );
      if (response.status === 201) {
        removeAllToasts();
        if (onClose) {
          onClose();
        }
        if (onSuccess) {
          onSuccess();
        } else {
          successToast('Member(s) added successfully!');
        }
        return;
      } else if (response.status === 200) {
        errorToast('Member(s) already exist!');
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Add Member(s).' });
    }
    setPreventSubmit(false);
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Container mb="xs">
        <Title variant="darkGrey" fontWeight="500">
          Add new member
        </Title>
      </Container>
      <Container maxHeight="280px" overflow={state.length < 4 ? 'initial' : 'auto'} mb="xs">
        {state.map((member, index) => (
          <Container
            key={index}
            flexDirection="row"
            alignItems="flex-end"
            gap="xs"
            px="s"
            py="xs"
            mb="xs"
            bg="#f5f9ff"
            borderRadius="12px"
          >
            <Container>
              <Label htmlFor={`email-${index}`} mb="xs">
                Email
              </Label>
              <TextInput
                id={`email-${index}`}
                name="email"
                data-index={index}
                type="email"
                variant="minimal"
                placeholder="Email"
                value={member.email}
                onChange={handleChange}
                onBlur={handleEmailBlur}
                required
              />
            </Container>
            <Container>
              <Label htmlFor={`role-${index}`} mb="xs">
                Role
              </Label>
              <Select
                id={`role-${index}`}
                testId="role"
                placeholder="Role"
                caretColor="primary"
                variant="minimal"
                options={MemberRoles}
                value={member.role}
                onChange={(option) =>
                  handleChange({
                    target: {
                      name: 'role',
                      dataset: { index },
                      value: option.value,
                    },
                  })
                }
                required
              />
            </Container>
            <Container>
              <Label htmlFor={`name-${index}`} mb="xs">
                User name
              </Label>
              <TextInput
                disabled={member.isNameDisabled}
                id={`name-${index}`}
                name="name"
                data-index={index}
                variant="minimal"
                placeholder="User name"
                value={member.name}
                onChange={handleChange}
                required
                autoFocus
              />
            </Container>
            <Container width="auto" pr={index === 0 && 's'}>
              {index > 0 && (
                <IconButton
                  variant="transparent"
                  src="/assets/img/icons/close-red.svg"
                  alt="remove"
                  size="14px"
                  opacity="0.8"
                  opacityOnHover="1"
                  mb="xs"
                  onClick={() => removeMember(index)}
                />
              )}
            </Container>
          </Container>
        ))}
      </Container>
      <IconButton
        data-testid="member-plus"
        icon="plus-white"
        iconProps={{ size: '12px' }}
        width="90px"
        height="30px"
        borderRadius="3px"
        bg="primary"
        mb="m"
        onClick={addMember}
      />
      <Container flexDirection="row" justifyContent="flex-end" mt="xxs">
        <Button
          variant="contrast-primary"
          width="30%"
          disabled={preventSubmit}
          onClick={onClose}
          boxShadow="none"
          mr="s"
        >
          Close
        </Button>
        <Button type="submit" width="30%" disabled={preventSubmit}>
          Add Member{state.length > 1 && 's'}
        </Button>
      </Container>
    </Form>
  );
};
